import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useProCheckout } from "@/components/ProUpsell";
import { Button } from "@/components/primitives";

const PERKS = [
  "Unlimited messaging & connections",
  "Full access to Opportunities & Events",
  "Daily Rewards check-in & points",
];

// App-wide prompt shown once a member's promotional Individual Plus period has ended.
export default function PromoExpiredDialog() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const checkout = useProCheckout();
  const decline = useMutation({
    mutationFn: () => api("/promo/decline", { method: "POST" }),
    onSuccess: async () => { setDismissed(true); await refresh(); },
    onError: (e) => toast.error(e.message),
  });

  if (!user?.promo_expired_prompt || dismissed) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm" data-testid="promo-expired-dialog">
      <div className="w-full max-w-md rounded-3xl border border-brand/30 bg-surface p-7 relative overflow-hidden animate-rise">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand/10 blur-2xl" />
        <div className="relative">
          <h2 className="font-serif text-2xl text-ink">Your free access has ended</h2>
          <p className="text-muted-foreground mt-1.5 text-sm">Your promotional Individual Plus period is over. Continue with Individual Plus to keep full access, or stay on the Individual Free tier.</p>
          <ul className="mt-4 flex flex-col gap-2">
            {PERKS.map((p) => <li key={p} className="flex items-start gap-2.5 text-sm text-ink"><Check className="w-4 h-4 text-brand shrink-0 mt-0.5" />{p}</li>)}
          </ul>
          <div className="mt-6 flex flex-col gap-2">
            <Button data-testid="promo-continue-plus" loading={checkout.isPending} onClick={() => checkout.mutate()}>
              Continue with Individual Plus
            </Button>
            <Button variant="ghost" data-testid="promo-decline" loading={decline.isPending} onClick={() => decline.mutate()}>
              No thanks, stay on Free
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
